import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, MapPin, Sparkles } from "lucide-react";
import { publicApi, API } from "@/lib/api";
import JanteLogo from "@/components/JanteLogo";

const JANTE_TRAVEL_LOGO =
  "https://customer-assets.emergentagent.com/job_sasha-host-flow/artifacts/hb7r80co_Jant%C3%A9%20Travel%20Concierges.png";
const SASHA_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_sasha-host-flow/artifacts/gs8ak51e_image.png";

export default function Guest() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    publicApi
      .get(`/guest/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(e?.response?.data?.detail || "This property is not available.");
      });
  }, [id]);

  if (error) {
    return (
      <main className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <JanteLogo size={28} className="mx-auto" />
          <h1 className="font-serif-display text-3xl text-[#2A1F1A] mt-6">
            Not available
          </h1>
          <p className="mt-3 text-sm text-[#6E6E70]">{error}</p>
          <Link
            to="/"
            className="btn-secondary mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-none"
          >
            Return home
          </Link>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="min-h-screen bg-[#FAF9F6] flex items-center justify-center text-sm text-[#6E6E70]">
        Loading your welcome…
      </main>
    );
  }

  return (
    <main data-testid="guest-page" className="bg-[#FAF9F6] min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Header */}
          <div className="flex items-center gap-3">
            <JanteLogo size={22} />
            <span className="eyebrow">A Janté welcome</span>
          </div>

          <h1
            data-testid="guest-property-name"
            className="font-serif-display mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2A1F1A]"
          >
            Welcome to
            <br />
            <span className="italic text-[#C03028]">
              {data.property_name}
            </span>
          </h1>
          <p className="mt-5 text-base lg:text-lg text-[#6E6E70] max-w-xl">
            Hosted with care by <strong className="text-[#2A1F1A]">{data.host_first_name}</strong>{" "}
            · Curated under the <strong className="text-[#2A1F1A]">{data.tier_name}</strong> service of Sasha.host.
          </p>
        </motion.div>

        {/* Host avatar + brief */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="border border-[#E3DED5] bg-white p-6">
              <div className="eyebrow">Your host</div>
              <div className="mt-5 aspect-square bg-[#F2EFE9] relative overflow-hidden">
                {data.avatar_file_id ? (
                  <img
                    src={`${API}/files/${data.avatar_file_id}/download`}
                    alt={`${data.host_first_name}'s avatar`}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="guest-avatar-img"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-serif-display text-7xl text-[#C03028] italic">
                      {data.host_first_name?.[0] || "S"}
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-5 font-serif-display text-2xl text-[#2A1F1A]">
                {data.host_first_name}
              </div>
              {data.avatar_brief && (
                <p
                  data-testid="guest-avatar-brief"
                  className="mt-3 text-sm text-[#6E6E70] leading-relaxed italic"
                >
                  &ldquo;{data.avatar_brief}&rdquo;
                </p>
              )}
              {data.brand_logo_file_id && (
                <div className="mt-5 pt-5 border-t border-[#E3DED5]">
                  <div className="eyebrow">Property mark</div>
                  <img
                    src={`${API}/files/${data.brand_logo_file_id}/download`}
                    alt="Property logo"
                    className="mt-3 h-16 w-auto object-contain"
                    data-testid="guest-brand-logo"
                  />
                </div>
              )}
            </div>
          </motion.div>

          {/* Sasha intro + Janté Travel */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="border border-[#E3DED5] bg-white p-6 lg:p-8">
              <div className="flex items-center gap-4">
                <img
                  src={SASHA_PORTRAIT}
                  alt="Sasha"
                  className="w-16 h-16 object-cover rounded-none ring-2 ring-[#C03028]/20"
                  data-testid="guest-sasha-portrait"
                />
                <div>
                  <div className="eyebrow">Concierge</div>
                  <div className="font-serif-display italic text-2xl text-[#2A1F1A] leading-none">
                    Sasha
                  </div>
                  <div className="text-xs text-[#8E8F90] mt-1">
                    Your hosting concierge
                  </div>
                </div>
              </div>
              <p className="mt-6 text-[#2A1F1A] leading-relaxed">
                <span className="font-serif-display italic text-2xl text-[#C03028]">
                  &ldquo;
                </span>
                Hello — I&apos;m Sasha. On behalf of {data.host_first_name}, welcome to
                {" "}{data.property_name}. Whether you need a quiet recommendation, a
                last-minute booking or anything between, our Janté Travel
                concierges are quietly on hand.
              </p>
            </div>

            {/* Janté Travel Concierges CTA */}
            <a
              href="https://AssetsRealEstateConsulting.com"
              target="_blank"
              rel="noreferrer"
              data-testid="guest-jante-travel-cta"
              className="mt-6 block border border-[#E3DED5] bg-[#2A1F1A] text-[#FAF9F6] p-6 lg:p-8 group hover:bg-[#1A1310] transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
                <img
                  src={JANTE_TRAVEL_LOGO}
                  alt="Janté Travel Concierges"
                  className="h-24 w-auto object-contain bg-[#FAF9F6] p-3"
                />
                <div className="flex-1">
                  <div
                    className="eyebrow"
                    style={{ color: "#B0895C" }}
                  >
                    Travel · Reservations · Experiences
                  </div>
                  <div className="font-serif-display text-3xl mt-2 italic">
                    Janté Travel Concierges
                  </div>
                  <p className="text-sm text-[#FAF9F6]/70 mt-3 max-w-md">
                    Bookings, transfers, dining, and curated experiences — taken
                    care of with quiet precision.
                  </p>
                </div>
                <Plane size={28} className="text-[#C03028] hidden md:block group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border border-[#E3DED5] bg-white p-5 flex items-start gap-3">
                <MapPin size={18} className="text-[#C03028] mt-0.5 shrink-0" />
                <div>
                  <div className="eyebrow">Local recommendations</div>
                  <div className="text-sm text-[#2A1F1A] mt-1">
                    Curated by your host
                  </div>
                </div>
              </div>
              <div className="border border-[#E3DED5] bg-white p-5 flex items-start gap-3">
                <Sparkles size={18} className="text-[#C03028] mt-0.5 shrink-0" />
                <div>
                  <div className="eyebrow">Bespoke experiences</div>
                  <div className="text-sm text-[#2A1F1A] mt-1">
                    Crafted on request
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-12 text-center text-xs text-[#8E8F90]">
          A Sasha.host welcome · Powered by Janté Lux Hotels
        </div>
      </div>
    </main>
  );
}
